import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  LogarithmicScale,
  PointElement,
  TimeScale,
  Tooltip as ChartTooltip,
} from 'chart.js';
import moment from 'moment';
import React from 'react';
import { Bar, Line, Scatter } from 'react-chartjs-2';
import { toast } from 'sonner';
import type {
  AdminStatsFilters,
  AdminStatsResponse,
  AgentLeaderboardRow,
  ApnBucketStat,
  ApnEngagementPoint,
  CampaignLeaderboardRow,
  Series,
  TouchPerformanceRow,
} from 'types/admin-stats';
import { ECampaignStatus } from 'types/campaign';
import 'chartjs-adapter-moment';

import {
  Box, Button, Chip, Grid, Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tabs, TextField, Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { alpha } from '@material-ui/core/styles/colorManipulator';
import ReplayRoundedIcon from '@material-ui/icons/ReplayRounded';
import { Autocomplete } from '@material-ui/lab';

import CustomButton from 'creative-components/CustomButtons/Button';

import DashboardSection from 'components/DashboardSection/DashboardSection';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';

import { getAgentUsers } from 'utils/api';
import { fetchAdminStats } from 'utils/api/admin-stats';
import { enumToReadableName, numberWithCommas, showAPIErrorAlert } from 'utils/lib';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  TimeScale,
  PointElement,
  LineElement,
  BarElement,
  ChartTooltip,
  Legend,
  Filler,
);

const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

const buildDefaultFilters = (): AdminStatsFilters => {
  const end = moment().utc().endOf('day');
  const start = end.clone().subtract(89, 'days').startOf('day');

  return {
    startDate: start.toDate(),
    endDate: end.toDate(),
  };
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: theme.spacing(4),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  headerTitle: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  lastUpdated: {
    color: theme.palette.grayScale4?.main ?? theme.palette.grey[600],
    fontSize: 14,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  filtersCard: {
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(3),
    borderRadius: 16,
  },
  filtersHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1.5),
  },
  filterGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  },
  filtersFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    gap: theme.spacing(1.5),
  },
  summaryGrid: {
    marginBottom: theme.spacing(3),
  },
  summaryCard: {
    height: '100%',
    padding: theme.spacing(2),
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  summaryLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.grayScale4?.main ?? theme.palette.grey[700],
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  summaryValue: {
    fontSize: 28,
    fontWeight: 600,
    color: theme.palette.grayScale11?.main ?? theme.palette.text.primary,
  },
  deltaPositive: {
    color: theme.palette.success.main,
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  deltaNegative: {
    color: theme.palette.error.main,
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  deltaNeutral: {
    color: theme.palette.grayScale4?.main ?? theme.palette.grey[600],
    fontWeight: 500,
  },
  breakdownChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  noteText: {
    color: theme.palette.grayScale4?.main ?? theme.palette.grey[600],
  },
  chartCard: {
    height: '100%',
    padding: theme.spacing(2),
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  chartBody: {
    flex: 1,
    position: 'relative',
    minHeight: 280,
  },
  emptyState: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: theme.palette.grayScale4?.main ?? theme.palette.grey[500],
    padding: theme.spacing(2),
  },
  leaderboardsCard: {
    padding: theme.spacing(2.5),
    borderRadius: 16,
    marginBottom: theme.spacing(3),
  },
  tableContainer: {
    marginTop: theme.spacing(1.5),
  },
  clickableRow: {
    cursor: 'pointer',
  },
  scatterWrapper: {
    height: 360,
    position: 'relative',
  },
  correlationBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  presetButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  sectionSpacing: {
    marginBottom: theme.spacing(3),
  },
  errorCard: {
    padding: theme.spacing(2),
    borderRadius: 16,
    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
}));

const hasSeriesValues = (series: Series[] | undefined) => {
  if (!series || !series.length) return false;

  return series.some((s) => s.points && s.points.some((p) => p.value !== 0));
};

const toPercent = (value: number | null | undefined, fractionDigits = 1) => {
  if (value === null || value === undefined) return '--';
  return `${(value * 100).toFixed(fractionDigits)}%`;
};

const toNumber = (value: number | null | undefined, fractionDigits = 0) => {
  if (value === null || value === undefined) return '--';
  if (fractionDigits > 0) return value.toFixed(fractionDigits);
  return numberFormatter.format(value);
};

const AdminStatsView: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [agents, setAgents] = React.useState<any[]>([]);
  const [isLoadingAgents, setIsLoadingAgents] = React.useState(false);

  const [filters, setFilters] = React.useState<AdminStatsFilters>(() => buildDefaultFilters());
  const [appliedFilters, setAppliedFilters] = React.useState<AdminStatsFilters>(() => buildDefaultFilters());

  const filtersRef = React.useRef<AdminStatsFilters>(filters);
  React.useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const [stats, setStats] = React.useState<AdminStatsResponse | null>(null);

  const [isLoading, setIsLoading] = React.useState(false);

  const [error, setError] = React.useState<string | null>(null);
  const [leaderboardTab, setLeaderboardTab] = React.useState(0);
  const [campaignPage, setCampaignPage] = React.useState(0);
  const [campaignRowsPerPage, setCampaignRowsPerPage] = React.useState(25);
  const [agentPage, setAgentPage] = React.useState(0);
  const [agentRowsPerPage, setAgentRowsPerPage] = React.useState(25);
  const [touchPage, setTouchPage] = React.useState(0);
  const [touchRowsPerPage, setTouchRowsPerPage] = React.useState(25);

  React.useEffect(() => {
    (async () => {
      try {
        setIsLoadingAgents(true);
        const response = await getAgentUsers();

        setAgents(response?.agents ?? []);
      } catch (err) {
        setAgents([]);
        showAPIErrorAlert(err);
      } finally {
        setIsLoadingAgents(false);
      }
    })();
  }, []);

  const fetchStats = React.useCallback(async (activeFilters: AdminStatsFilters) => {
    setIsLoading(true);

    const toastId = toast.loading('Compiling stats may take up to a few minutes...');

    try {
      setError(null);
      const data = await fetchAdminStats(activeFilters);
      setStats(data);

      setCampaignPage(0);
      setAgentPage(0);
      setTouchPage(0);
    } catch (err) {
      setError('Unable to load admin stats.');
      showAPIErrorAlert(err);
    } finally {
      setIsLoading(false);
      toast.dismiss(toastId);
    }
  }, []);

  React.useEffect(() => {
    fetchStats(appliedFilters);
  }, [appliedFilters]);

  const palette = React.useMemo(() => [
    theme.palette.primary.main,
    '#0EA5E9',
    '#10B981',
    '#F59E0B',
    '#6366F1',
    '#F97316',
    '#EF4444',
    '#84CC16',
  ], [theme.palette.primary.main]);

  const campaignStatusOptions = React.useMemo(() => [ECampaignStatus.Active, ECampaignStatus.Paused, ECampaignStatus.Completed, ECampaignStatus.Cancelled]
    .map((value) => ({
      value,
      label: enumToReadableName(value),
    })), []);

  const agentOptions = React.useMemo(() => agents.map((agent) => ({
    value: agent._id,
    label: `${agent.firstName ?? ''} ${agent.lastName ?? ''}`.trim() || agent.email || agent._id,
  })).sort((a, b) => a.label.localeCompare(b.label)),
  [agents]);

  const presetRanges = React.useMemo(() => ([
    {
      key: '7d',
      label: 'Last 7 Days',
      start: moment().utc().subtract(6, 'days').startOf('day'),
      end: moment().utc().endOf('day'),
    },
    {
      key: '30d',
      label: 'Last 30 Days',
      start: moment().utc().subtract(29, 'days').startOf('day'),
      end: moment().utc().endOf('day'),
    },
    {
      key: '90d',
      label: 'Last 90 Days',
      start: moment().utc().subtract(89, 'days').startOf('day'),
      end: moment().utc().endOf('day'),
    },
  ]), []);

  const activePreset = React.useMemo(() => {
    if (!filters.startDate || !filters.endDate) return null;
    const start = moment(filters.startDate);
    const end = moment(filters.endDate);

    const match = presetRanges.find((preset) => start.isSame(preset.start, 'day') && end.isSame(preset.end, 'day'));

    return match?.key ?? null;
  }, [filters.startDate, filters.endDate, presetRanges]);

  const handlePresetClick = (key: string) => {
    const preset = presetRanges.find((p) => p.key === key);
    if (!preset) return;

    setFilters((prev) => ({
      ...prev,
      startDate: preset.start.toDate(),
      endDate: preset.end.toDate(),
    }));
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const nextStart = value ? moment(value).utc().startOf('day') : null;

    setFilters((prev) => {
      const end = prev.endDate ? moment(prev.endDate) : null;
      let nextEnd = end;

      if (nextStart && end && nextStart.isAfter(end)) {
        nextEnd = nextStart.clone().endOf('day');
      }

      return {
        ...prev,
        startDate: nextStart ? nextStart.toDate() : undefined,
        endDate: nextEnd ? nextEnd.toDate() : prev.endDate,
      };
    });
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const nextEnd = value ? moment(value).utc().endOf('day') : null;

    setFilters((prev) => {
      const start = prev.startDate ? moment(prev.startDate) : null;
      let nextStart = start;

      if (nextEnd && start && nextEnd.isBefore(start)) {
        nextStart = nextEnd.clone().startOf('day');
      }

      return {
        ...prev,
        startDate: nextStart ? nextStart.toDate() : prev.startDate,
        endDate: nextEnd ? nextEnd.toDate() : undefined,
      };
    });
  };

  const handleResetFilters = () => {
    setFilters(buildDefaultFilters());
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
  };

  const renderEmptyState = (message: string) => (
    <div className={classes.emptyState}>
      <Typography variant="body2">{message}</Typography>
    </div>
  );

  const renderSummaryCards = () => {
    const cards = stats?.summaryCards;
    if (!cards || !cards.length) return null;

    return (
      <Grid container spacing={2} className={classes.summaryGrid}>
        {cards.map((card) => {
          const {
            id, value, previousValue, format,
          } = card;

          let formattedValue = '--';
          if (value !== null && value !== undefined) {
            formattedValue = format === 'percent' ? toPercent(value, 4) : toNumber(value);
          }

          let deltaContent = null;
          if (previousValue !== undefined && previousValue !== null && value !== null && value !== undefined) {
            const delta = value - previousValue;
            const formattedDelta = format === 'percent'
              ? `${Math.abs(delta * 100).toFixed(4)} pp`
              : toNumber(Math.abs(delta));

            if (delta > 0) {
              deltaContent = (
                <Typography variant="body2" className={classes.deltaPositive}>
                  ▲
                  {formattedDelta}
                </Typography>
              );
            } else if (delta < 0) {
              deltaContent = (
                <Typography variant="body2" className={classes.deltaNegative}>
                  ▼
                  {formattedDelta}
                </Typography>
              );
            } else if (!['activeCampaigns', 'pausedCampaigns'].includes(id)) { // Don't show "No change" for certain cards
              deltaContent = (
                <Typography variant="body2" className={classes.deltaNeutral}>
                  No change
                </Typography>
              );
            }
          }

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={card.id}>
              <Paper className={classes.summaryCard} elevation={0}>
                <Typography className={classes.summaryLabel}>
                  {card.label}
                </Typography>
                <Typography className={classes.summaryValue}>
                  {formattedValue}
                </Typography>
                {deltaContent}
                {card.breakdown && card.breakdown.length > 0 && (
                  <div className={classes.breakdownChips}>
                    {card.breakdown.map((item) => (
                      <Chip
                        key={item.key}
                        label={`${item.label}: ${toNumber(item.value)}`}
                        size="small"
                      />
                    ))}
                  </div>
                )}
                {card.note && (
                  <Typography variant="caption" className={classes.noteText}>
                    {card.note}
                  </Typography>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const buildTimeSeriesData = (series: Series[], fill = false) => ({
    datasets: series.map((s, index) => {
      const color = palette[index % palette.length];

      return {
        label: s.label,
        data: s.points.map((point) => ({
          x: point.date,
          y: point.value,
        })),
        borderColor: color,
        backgroundColor: fill ? alpha(color, 0.2) : color,
        pointBackgroundColor: color,
        fill,
        tension: 0.35,
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 4,
      };
    }),
  });

  const baseTimeSeriesOptions = React.useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    scales: {
      x: {
        type: 'time' as const,
        time: {
          tooltipFormat: 'MMM D, YYYY',
        },
        grid: {
          color: alpha(theme.palette.grayScale6?.main ?? theme.palette.grey[300], 0.4),
        },
        ticks: {
          maxTicksLimit: 10,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: alpha(theme.palette.grayScale6?.main ?? theme.palette.grey[300], 0.4),
        },
        ticks: {
          precision: 0,
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${toNumber(value)}`;
          },
        },
      },
    },
  }), [theme.palette.grayScale6, theme.palette.grey]);

  const stackedTimeSeriesOptions = React.useMemo(() => ({
    ...baseTimeSeriesOptions,
    scales: {
      ...baseTimeSeriesOptions.scales,
      x: {
        ...baseTimeSeriesOptions.scales.x,
        stacked: true,
      },
      y: {
        ...baseTimeSeriesOptions.scales.y,
        stacked: true,
      },
    },
  }), [baseTimeSeriesOptions]);

  const buildGroupedBarData = (series: Series[]) => {
    const labels = Array.from(new Set(series.flatMap((s) => s.points.map((p) => p.date)))).sort();

    // Filter out any series where all points have a value of 0
    const filteredSeries = series.filter((s) => s.points.some((p) => p.value !== 0));

    return {
      labels: labels.map((label) => moment(label).format('MMM D')),
      datasets: filteredSeries.map((s, index) => {
        const color = palette[index % palette.length];

        return {
          label: s.label,
          backgroundColor: alpha(color, 0.7),
          borderColor: color,
          borderWidth: 1,
          data: labels.map((label) => {
            const point = s.points.find((p) => p.date === label);
            return point ? point.value : 0;
          }),
        };
      }),
    };
  };

  const groupedBarOptions = React.useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: false,
      },
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${toNumber(context.parsed.y)}`,
        },
      },
    },
  }), []);

  const buildScatterData = (points: ApnEngagementPoint[]) => {
    const grouped = points.reduce<Record<string, ApnEngagementPoint[]>>((acc, point) => {
      const status = point.status ?? 'Unknown';
      if (!acc[status]) acc[status] = [];
      acc[status].push(point);
      return acc;
    }, {});

    const statusColors: Record<string, string> = {
      ACTIVE: theme.palette.success.main,
      PAUSED: theme.palette.warning.main,
      COMPLETED: theme.palette.primary.main,
      CANCELLED: theme.palette.error.main,
    };

    return {
      datasets: Object.entries(grouped).map(([status, statusPoints], index) => {
        const key = status.toUpperCase();
        const color = statusColors[key] || palette[index % palette.length];

        return {
          label: enumToReadableName(status),
          backgroundColor: alpha(color, 0.6),
          borderColor: color,
          data: statusPoints.map((point) => ({
            x: point.apnCount,
            y: point.engagementRate,
            campaignName: point.campaignName,
            agentName: point.agentName,
            responses: point.responses,
            apnCount: point.apnCount,
            status: point.status,
          })),
        };
      }),
    };
  };

  const scatterOptions = React.useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: 'APN Count',
        },
        ticks: {
          precision: 0,
        },
      },
      y: {
        title: {
          display: true,
          text: 'Engagement Rate',
        },
        ticks: {
          callback: (value: number) => `${(value * 100).toFixed()}%`,
        },
        min: 0,
      },
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        align: 'center' as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const raw = context.raw as any;
            return [
              `${raw.campaignName}`,
              `Agent: ${raw.agentName}`,
              `Engagement Rate: ${(raw.y * 100).toFixed(1)}%`,
              `Engagements: ${raw.responses}`,
              `APNs: ${raw.apnCount}`,
            ];
          },
        },
      },
    },
  }), []);

  const renderCharts = () => {
    const charts = stats?.charts;
    if (!charts) return null;

    const chartDefinitions: {
      key: string;
      title: string;
      series: Series[] | undefined;
      emptyMessage: string;
      type: 'stackedArea' | 'line' | 'bar';
      md?: 6 | 12;
      lg?: 6 | 12;
    }[] = [
      {
        key: 'campaignLaunchVelocity',
        title: 'Campaign Launch Velocity',
        series: charts.campaignLaunchVelocity,
        emptyMessage: 'No campaign launches within the selected range.',
        type: 'line',
        md: 6,
        lg: 6,
      },
      {
        key: 'activeAgents',
        title: 'Agents With Active Campaigns',
        series: charts.activeAgents,
        emptyMessage: 'No agent activity recorded.',
        type: 'line',
        md: 6,
        lg: 6,
      },
      {
        key: 'leadResponsesByType',
        title: 'All Engagements by Type',
        series: charts.leadResponsesByType,
        emptyMessage: 'No responses in selected range.',
        type: 'stackedArea',
        md: 12,
        lg: 12,
      },
      {
        key: 'landingInteractions',
        title: 'Landing Page Interactions',
        series: charts.landingInteractions,
        emptyMessage: 'No landing page interactions recorded.',
        type: 'bar',
        md: 12,
        lg: 12,
      },
      {
        key: 'emailEngagement',
        title: 'Email Engagement',
        series: charts.emailEngagement,
        emptyMessage: 'No email engagement recorded.',
        type: 'line',
        md: 12,
        lg: 12,
      },
    ];

    return (
      <Grid container spacing={2} className={classes.sectionSpacing}>
        {chartDefinitions.map((chart) => (
          <Grid item xs={12} md={chart.md ?? 6} lg={chart.lg ?? 6} key={chart.key}>
            <Paper className={classes.chartCard} elevation={0}>
              <DashboardSection sectionName={chart.title} />
              <div className={classes.chartBody}>
                {hasSeriesValues(chart.series)
                  ? (() => {
                    if (!chart.series) return null;

                    switch (chart.type) {
                      case 'stackedArea':
                        return (
                          <Line
                            data={buildTimeSeriesData(chart.series, true)}
                            options={stackedTimeSeriesOptions}
                          />
                        );
                      case 'bar':
                        return (
                          <Bar
                            data={buildGroupedBarData(chart.series)}
                            options={groupedBarOptions}
                          />
                        );
                      default:
                        return (
                          <Line
                            data={buildTimeSeriesData(chart.series, false)}
                            options={baseTimeSeriesOptions}
                          />
                        );
                    }
                  })()
                  : renderEmptyState(chart.emptyMessage)}
              </div>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  // TODO: REMOVE WHEN WE ADD OUR TABLES AND OUR OWN SORTING???
  const campaignRows = React.useMemo(() => {
    const rows = stats?.leaderboards?.campaigns ?? [];
    return [...rows].sort((a, b) => b.responseRatePerThousandApns - a.responseRatePerThousandApns);
  }, [stats?.leaderboards?.campaigns]);

  const agentRows = React.useMemo(() => {
    const rows = stats?.leaderboards?.agents ?? [];
    return [...rows].sort((a, b) => b.totalResponses - a.totalResponses);
  }, [stats?.leaderboards?.agents]);

  const touchRows = React.useMemo(() => {
    const rows = stats?.leaderboards?.touches ?? [];
    return [...rows].sort((a, b) => b.responseRate - a.responseRate);
  }, [stats?.leaderboards?.touches]);

  const renderCampaignLeaderboard = (rows: CampaignLeaderboardRow[]) => {
    if (!rows.length) return renderEmptyState('No campaign leaderboard data available.');

    const paginated = rows.slice(campaignPage * campaignRowsPerPage, (campaignPage * campaignRowsPerPage) + campaignRowsPerPage);

    return (
      <>
        <TableContainer className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Campaign</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Engagements</TableCell>
                <TableCell align="right">Engagements / Day</TableCell>
                <TableCell align="right" style={{ fontWeight: 700 }}>Engagements / 1k APNs</TableCell>
                <TableCell align="right">APN Count</TableCell>
                <TableCell align="right">Last Engagement</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((row) => (
                <TableRow
                  key={row.campaignId}
                  hover
                  className={classes.clickableRow}
                  onClick={() => window.open(`/dashboard/campaigns/${row.campaignId}`, '_blank', 'noopener,noreferrer')}
                >
                  <TableCell>{row.campaignName}</TableCell>
                  <TableCell>{row.agentName}</TableCell>
                  <TableCell>{enumToReadableName(row.status)}</TableCell>
                  <TableCell align="right">{toNumber(row.responses)}</TableCell>
                  <TableCell align="right">{row.responsesPerActiveDay.toFixed(2)}</TableCell>
                  <TableCell align="right">{row.responseRatePerThousandApns.toFixed(1)}</TableCell>
                  <TableCell align="right">{toNumber(row.apnCount)}</TableCell>
                  <TableCell align="right">{row.lastResponseAt ? moment(row.lastResponseAt).fromNow() : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={rows.length}
          page={campaignPage}
          rowsPerPage={campaignRowsPerPage}
          onPageChange={(_, newPage) => setCampaignPage(newPage)}
          onRowsPerPageChange={(event) => {
            const value = parseInt(event.target.value, 10);
            setCampaignRowsPerPage(value);
            setCampaignPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </>
    );
  };

  const renderAgentLeaderboard = (rows: AgentLeaderboardRow[]) => {
    if (!rows.length) return renderEmptyState('No agent leaderboard data available.');

    const paginated = rows.slice(agentPage * agentRowsPerPage, (agentPage * agentRowsPerPage) + agentRowsPerPage);

    return (
      <>
        <TableContainer className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell align="right">Active Campaigns</TableCell>
                <TableCell align="right" style={{ fontWeight: 700 }}>Total Engagements</TableCell>
                <TableCell align="right">Email Reply Rate</TableCell>
                <TableCell align="right">Landing Page Conversion Rate</TableCell>
                <TableCell align="right">Engagements / Campaign</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((row) => (
                <TableRow key={row.agentId} hover>
                  <TableCell>{row.agentName}</TableCell>
                  <TableCell align="right">{toNumber(row.campaignsActive)}</TableCell>
                  <TableCell align="right">{toNumber(row.totalResponses)}</TableCell>
                  <TableCell align="right">{toPercent(row.emailReplyRate, 2)}</TableCell>
                  <TableCell align="right">{toPercent(row.landingConversionRate)}</TableCell>
                  <TableCell align="right">{row.responsesPerCampaign.toFixed(1)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={rows.length}
          page={agentPage}
          rowsPerPage={agentRowsPerPage}
          onPageChange={(_, newPage) => setAgentPage(newPage)}
          onRowsPerPageChange={(event) => {
            const value = parseInt(event.target.value, 10);
            setAgentRowsPerPage(value);
            setAgentPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </>
    );
  };

  const renderTouchLeaderboard = (rows: TouchPerformanceRow[]) => {
    if (!rows.length) return renderEmptyState('No touch performance data available.');

    const paginated = rows.slice(touchPage * touchRowsPerPage, (touchPage * touchRowsPerPage) + touchRowsPerPage);

    return (
      <>
        <TableContainer className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Touch Name</TableCell>
                <TableCell>Touch Type</TableCell>
                <TableCell>Event Type</TableCell>
                <TableCell>Campaign</TableCell>
                <TableCell align="right">Sends</TableCell>
                <TableCell align="right">Engagements</TableCell>
                <TableCell align="right" style={{ fontWeight: 700 }}>Engagements Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((row) => (
                <TableRow
                  key={`${row.touchTriggerId}-${row.campaignId}`}
                  hover
                  className={classes.clickableRow}
                  onClick={() => window.open(`/dashboard/campaigns/${row.campaignId}`, '_blank', 'noopener,noreferrer')}
                >
                  <TableCell>{row.description}</TableCell>
                  <TableCell>{enumToReadableName(row.touchType)}</TableCell>
                  <TableCell>{row.eventType}</TableCell>
                  <TableCell>{`${row.campaignName} • ${row.agentName}`}</TableCell>
                  <TableCell align="right">{toNumber(row.sends)}</TableCell>
                  <TableCell align="right">{toNumber(row.responses)}</TableCell>
                  <TableCell align="right">{toPercent(row.responseRate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={rows.length}
          page={touchPage}
          rowsPerPage={touchRowsPerPage}
          onPageChange={(_, newPage) => setTouchPage(newPage)}
          onRowsPerPageChange={(event) => {
            const value = parseInt(event.target.value, 10);
            setTouchRowsPerPage(value);
            setTouchPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </>
    );
  };

  /*
  // TODO: CHANGE TO OUR NORMAL TABLES!!!

  add filters to tables??? and sorting???
if so remove bold table names, or set as default sort on frontend side?
  */
  const renderLeaderboards = () => (
    <Paper className={classes.leaderboardsCard} elevation={0}>
      <DashboardSection sectionName="Leaderboards" />

      <Tabs
        value={leaderboardTab}
        onChange={(_, value) => setLeaderboardTab(value)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
      >
        <Tab label={`Campaigns (${numberWithCommas(campaignRows.length)})`} />
        <Tab label={`Agents (${numberWithCommas(agentRows.length)})`} />
        <Tab label={`Touches (${numberWithCommas(touchRows.length)})`} />
      </Tabs>

      <div>
        {leaderboardTab === 0 && renderCampaignLeaderboard(campaignRows)}
        {leaderboardTab === 1 && renderAgentLeaderboard(agentRows)}
        {leaderboardTab === 2 && renderTouchLeaderboard(touchRows)}
      </div>
    </Paper>
  );

  const renderApnBuckets = (buckets: ApnBucketStat[]) => (
    <TableContainer className={classes.tableContainer}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Bucket</TableCell>
            <TableCell align="right">Campaigns</TableCell>
            <TableCell align="right">Avg Engagement Rate</TableCell>
            <TableCell>Best Campaign</TableCell>
            <TableCell align="right">Best Rate</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {buckets.map((bucket) => (
            <TableRow key={bucket.bucketLabel}>
              <TableCell>{bucket.bucketLabel}</TableCell>
              <TableCell align="right">{toNumber(bucket.campaigns)}</TableCell>
              <TableCell align="right">{toPercent(bucket.averageEngagementRate, 4)}</TableCell>
              <TableCell>
                {bucket.bestCampaignId ? (
                  <Button
                    color="primary"
                    size="small"
                    onClick={() => window.open(`/dashboard/campaigns/${bucket.bestCampaignId}`, '_blank', 'noopener,noreferrer')}
                  >
                    {bucket.bestCampaignName}
                    {bucket.bestCampaignAgentName ? ` • ${bucket.bestCampaignAgentName}` : ''}
                  </Button>
                ) : '—'}
              </TableCell>
              <TableCell align="right">{bucket.bestCampaignRate !== undefined ? toPercent(bucket.bestCampaignRate, 4) : '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderApnEngagement = () => {
    const apn = stats?.apnEngagement;
    if (!apn) return null;

    const hasScatter = apn.scatter && apn.scatter.length > 0;
    const hasBuckets = apn.buckets && apn.buckets.length > 0;

    if (!hasScatter && !hasBuckets) return null;

    return (
      <Grid container spacing={2} className={classes.sectionSpacing}>
        <Grid item xs={12}>
          <Paper className={classes.chartCard} elevation={0}>
            <DashboardSection sectionName="APN Engagement Scatter" />
            <div className={classes.scatterWrapper}>
              {hasScatter ? (
                <Scatter
                  data={buildScatterData(apn.scatter)}
                  options={scatterOptions as any}
                />
              ) : renderEmptyState('No APN engagement data available.')}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper className={classes.chartCard} elevation={0}>
            <DashboardSection sectionName="APN Buckets" />

            {hasBuckets ? renderApnBuckets(apn.buckets) : renderEmptyState('No APN bucket data available.')}

            <Box className={classes.correlationBox}>
              <Typography variant="subtitle2">Campaign & APN Count Correlation</Typography>
              <Typography variant="body2">
                {apn.correlation !== undefined && apn.correlation !== null ? (
                  <>
                    {apn.correlation.toFixed(2)}
                    {' '}
                    (
                    {apn.correlation > 0
                      ? 'Positive correlation'
                      : apn.correlation < 0
                        ? 'Negative correlation'
                        : 'No correlation'}
                    ) —
                    {' '}
                    {apn.correlation > 0.1
                      ? 'Campaigns with more APNs tend to have more lead responses.'
                      : apn.correlation < -0.1
                        ? 'Campaigns with fewer APNs tend to have more lead responses.'
                        : 'There is very little relationship between the number of APNs in a campaign and the number of lead responses.'}
                  </>
                ) : (
                  'Correlation not available for the selected filters.'
                )}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  return (
    <div className={classes.root}>
      <DashboardSection
        sectionName="Admin Stats"
        // Maybe in the future allow them to force refresh even the cached stats?
        // buttons={[
        //   <Button
        //     variant="outlined"
        //     startIcon={isRefreshing ? <CircularProgress size={18} /> : <RefreshRoundedIcon />}
        //     onClick={applyManualRefresh}
        //     disabled={isLoading}
        //   >
        //     Refresh
        //   </Button>,
        // ]}
      />

      <Typography component="h4">
        {stats?.meta?.lastUpdated ? `Last updated ${moment(stats.meta.lastUpdated).local().format('LLL')}` : ''}
      </Typography>

      <Paper className={classes.filtersCard} elevation={0}>
        <div className={classes.filtersHeader}>
          <div className={classes.controls}>
            <Button
              variant="text"
              startIcon={<ReplayRoundedIcon />}
              onClick={handleResetFilters}
              size="small"
            >
              Reset
            </Button>
            <div className={classes.presetButtons}>
              {presetRanges.map((preset) => (
                <Button
                  key={preset.key}
                  variant={activePreset === preset.key ? 'contained' : 'outlined'}
                  color={activePreset === preset.key ? 'primary' : 'default'}
                  onClick={() => handlePresetClick(preset.key)}
                  size="small"
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* TODO: USE AI TO UPDATE THESE TO MATERIAL PICKERS??? */}
        <div className={classes.filterGrid}>
          <TextField
            label="Start Date"
            type="date"
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.startDate ? moment(filters.startDate).format('YYYY-MM-DD') : ''}
            onChange={handleStartDateChange}
          />
          <TextField
            label="End Date"
            type="date"
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.endDate ? moment(filters.endDate).format('YYYY-MM-DD') : ''}
            onChange={handleEndDateChange}
          />
          <Autocomplete
            multiple
            getOptionLabel={(o: any) => o.label}
            options={campaignStatusOptions}
            value={campaignStatusOptions.filter((option) => filters.campaignStatuses?.includes(option.value))}
            onChange={(_, newValue) => setFilters((prev) => ({
              ...prev,
              campaignStatuses: newValue.length ? newValue.map((option) => option.value) : undefined,
            }))}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Campaign Status"
                size="small"
              />
            )}
          />
          <Autocomplete
            multiple
            getOptionLabel={(o: any) => o.label}
            options={agentOptions}
            loading={isLoadingAgents}
            value={agentOptions.filter((option) => filters.agentIds?.includes(option.value))}
            onChange={(_, newValue) => setFilters((prev) => ({
              ...prev,
              agentIds: newValue.length ? newValue.map((option) => option.value) : undefined,
            }))}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Agents"
                size="small"
              />
            )}
          />
        </div>

        <div className={classes.filtersFooter}>
          {/* Hacky check for dirtiness, but its okay */}
          {JSON.stringify(filters) !== JSON.stringify(appliedFilters) && (
            <CustomButton
              color="primary"
              onClick={handleApplyFilters}
            >
              Apply
            </CustomButton>
          )}
        </div>
      </Paper>

      {error && (
        <Paper className={classes.errorCard} elevation={0}>
          <Typography variant="body1">{error}</Typography>
          <div>
            <Button variant="outlined" color="primary" onClick={() => fetchStats(appliedFilters)}>
              Retry
            </Button>
          </div>
        </Paper>
      )}

      {stats ? (
        <>
          {isLoading && <LoadingIndicator modal />}

          {stats && (
            <>
              {renderSummaryCards()}
              {renderCharts()}
              {renderApnEngagement()}
              {renderLeaderboards()}
            </>
          )}
        </>
      ) : <LoadingIndicator />}
    </div>
  );
};

export default AdminStatsView;
